import styled from "@emotion/styled";
import { 
  Button, 
  Typography, 
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { postApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { forgotInitialValues } from "../../../constants";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { errorToast, successToast } from "../../../utils/helper";
import PublicIcon from "@mui/icons-material/Public";
import LockResetIcon from "@mui/icons-material/LockReset";
import LoginIcon from "@mui/icons-material/Login";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Theme colors
const PRIMARY_COLOR = "#7285b3";
const PRIMARY_DARK = "#5a6a94";

// Styled Components
const PageContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: "#f5f6fa",
  padding: "20px",
});

const ForgotCard = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px",
  borderRadius: "16px",
  maxWidth: "450px",
  width: "100%",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
});

const InputWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  marginBottom: "16px",
});

const StyledInput = styled("input")(({ hasError }) => ({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "12px",
  fontSize: "15px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: hasError ? "#d32f2f" : "#e0e0e0",
  fontFamily: "Poppins, sans-serif",
  backgroundColor: "#fafafa",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  "&:focus": {
    outline: "none",
    borderColor: hasError ? "#d32f2f" : PRIMARY_COLOR,
    backgroundColor: "#fff",
    boxShadow: `0 0 0 3px rgba(114, 133, 179, 0.15)`,
  },
  "&:disabled": {
    backgroundColor: "#eee",
    cursor: "not-allowed",
  },
  "&::placeholder": {
    color: "#aaa",
  },
}));

const StyledButton = styled(Button)({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "12px",
  color: "white",
  fontSize: "15px",
  fontWeight: 600,
  backgroundColor: PRIMARY_COLOR,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: PRIMARY_DARK,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px rgba(114, 133, 179, 0.4)`,
  },
  "&:disabled": {
    backgroundColor: "#ccc",
  },
});

const BackButton = styled(Button)({
  padding: "10px 20px",
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  color: "#666",
  borderColor: "#e0e0e0",
  "&:hover": {
    borderColor: PRIMARY_COLOR,
    color: PRIMARY_COLOR,
    backgroundColor: "rgba(114, 133, 179, 0.05)",
  },
});

const ErrorText = styled("span")({
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
  display: "block",
  paddingLeft: "4px",
});

const IconWrapper = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
  backgroundColor: "rgba(114, 133, 179, 0.1)",
});

const SuccessIconWrapper = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
  backgroundColor: "#e8f5e9",
});

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState(forgotInitialValues);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, values[name]);
  };

  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "username":
        if (!value || value.trim() === "") {
          error = "Username is required";
        }
        break;
      case "email":
        if (!value || value.trim() === "") {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "tenantName":
        if (!value || value.trim() === "") {
          error = "Tenant name is required";
        }
        break;
      default:
        break;
    }
    
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
    
    return error;
  };

  const validateAllFields = () => {
    const fieldsToValidate = ["username", "email", "tenantName"];
    let isValid = true;
    const newErrors = {};
    const newTouched = {};

    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      const error = validateField(field, values[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);
    
    return isValid;
  };

  const forgotPassword = useMutation({
    mutationFn: () => postApiServices(apiRoutes.forgotPassword, values),
    onSuccess: async ({ data }) => {
      setIsSuccess(true);
      successToast("Password reset email sent successfully!");
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data || 
                          "Something went wrong";
      errorToast(typeof errorMessage === "string" ? errorMessage : "Failed to send reset email");
    },
  });

  const handleSubmit = () => {
    const isValid = validateAllFields();
    
    if (!isValid) {
      errorToast("Please fill all required fields correctly");
      return;
    }
    
    forgotPassword.mutate();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTE_PATHS.LOGIN);
  };

  const handleTryAgain = () => {
    setIsSuccess(false);
    setValues(forgotInitialValues);
    setTouched({});
    setErrors({});
  };

  // Success View
  if (isSuccess) {
    return (
      <PageContainer>
        <ForgotCard elevation={0}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
            <Typography
              variant="h5"
              sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
            >
              Premierdis
            </Typography>
          </Box>

          <SuccessIconWrapper>
            <CheckCircleOutlineIcon sx={{ fontSize: 45, color: "#4caf50" }} />
          </SuccessIconWrapper>

          <Typography
            sx={{ 
              fontSize: "24px", 
              fontWeight: 600, 
              mb: 1, 
              fontFamily: "Poppins",
              color: "#333",
              textAlign: "center",
            }}
          >
            Check Your Email
          </Typography>

          <Typography
            sx={{ 
              fontSize: "14px", 
              mb: 3, 
              fontFamily: "Poppins",
              color: "#666",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            We've sent a password reset link to <br />
            <strong style={{ color: PRIMARY_COLOR }}>{values.email}</strong>
          </Typography>

          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              width: "100%",
              borderRadius: 2,
              "& .MuiAlert-icon": {
                color: PRIMARY_COLOR,
              },
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
              Didn't receive the email? Check your spam folder or make sure you entered the correct email address.
            </Typography>
          </Alert>

          <StyledButton 
            variant="contained" 
            onClick={handleBackToLogin}
            startIcon={<LoginIcon />}
            sx={{ mb: 2 }}
          >
            Back to Login
          </StyledButton>

          <Button
            variant="text"
            onClick={handleTryAgain}
            sx={{ 
              textTransform: "none",
              color: "#666",
              fontFamily: "Poppins",
              "&:hover": {
                color: PRIMARY_COLOR,
              },
            }}
          >
            Try with different email
          </Button>
        </ForgotCard>
      </PageContainer>
    );
  }

  // Form View
  return (
    <PageContainer>
      <ForgotCard elevation={0}>
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
          <Typography
            variant="h5"
            sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
          >
            Premierdis
          </Typography>
        </Box>

        <IconWrapper>
          <LockResetIcon sx={{ fontSize: 40, color: PRIMARY_COLOR }} />
        </IconWrapper>

        <Typography
          sx={{ 
            fontSize: "24px", 
            fontWeight: 600, 
            mb: 0.5, 
            fontFamily: "Poppins",
            color: "#333",
          }}
        >
          Forgot Password?
        </Typography>

        <Typography
          sx={{ 
            fontSize: "14px", 
            mb: 3, 
            fontFamily: "Poppins",
            color: "#888",
            textAlign: "center",
          }}
        >
          No worries! Enter your details and we'll send you a reset link.
        </Typography>

        {/* Form */}
        <Box sx={{ width: "100%" }}>
          {/* Username */}
          <InputWrapper>
            <StyledInput
              name="username"
              type="text"
              placeholder="Username *"
              value={values?.username || ""}
              onChange={onChange}
              onBlur={() => handleBlur("username")}
              onKeyPress={handleKeyPress}
              hasError={touched.username && errors.username}
              disabled={forgotPassword.isPending}
            />
            {touched.username && errors.username && (
              <ErrorText>{errors.username}</ErrorText>
            )}
          </InputWrapper>

          {/* Email */}
          <InputWrapper>
            <StyledInput
              name="email"
              type="email"
              placeholder="Email Address *"
              value={values?.email || ""}
              onChange={onChange}
              onBlur={() => handleBlur("email")}
              onKeyPress={handleKeyPress}
              hasError={touched.email && errors.email}
              disabled={forgotPassword.isPending}
            />
            {touched.email && errors.email && (
              <ErrorText>{errors.email}</ErrorText>
            )}
          </InputWrapper>

          {/* Tenant Name */}
          <InputWrapper>
            <StyledInput
              name="tenantName"
              type="text"
              placeholder="Tenant Name *"
              value={values?.tenantName || ""}
              onChange={onChange}
              onBlur={() => handleBlur("tenantName")}
              onKeyPress={handleKeyPress}
              hasError={touched.tenantName && errors.tenantName}
              disabled={forgotPassword.isPending}
            />
            {touched.tenantName && errors.tenantName && (
              <ErrorText>{errors.tenantName}</ErrorText>
            )}
          </InputWrapper>

          {/* Submit Button */}
          <StyledButton 
            variant="contained" 
            onClick={handleSubmit}
            disabled={forgotPassword.isPending}
            startIcon={!forgotPassword.isPending && <EmailOutlinedIcon />}
            sx={{ mt: 1 }}
          >
            {forgotPassword.isPending ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              "Send Reset Link"
            )}
          </StyledButton>
        </Box>

        {/* Divider */}
        <Box sx={{ width: "100%", my: 3, display: "flex", alignItems: "center" }}>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        {/* Back to Login */}
        <BackButton
          variant="outlined"
          onClick={handleBackToLogin}
          startIcon={<ArrowBackIcon />}
          fullWidth
        >
          Back to Login
        </BackButton>
      </ForgotCard>
    </PageContainer>
  );
};